import '../shared-types'

import cx from 'classnames'
import * as React from 'react'

import Icon, { IconProps } from '../icon'
import { IconSizes } from '../icon/icon'
import type { SpacingProps } from '../shared-types'
import Spacer from '../spacer'
import { filterSpacingProps } from '../spacer/utils'
import styles from './toggletip.module.scss'
import { VisuallyHidden } from '..'

export type Props = Readonly<
  SpacingProps & {
    description: React.ReactNode
    flipVertically?: boolean
    flipHorizontally?: boolean
    id: string
    containerClassName?: string
    bubbleClassName?: string
    onClick: () => void
    showToggletip: boolean
    iconColor?: IconProps['color']
    hideIcon?: boolean
    size?: IconSizes
  }
>

// This toggletip is controlled by the parent component and works when you click/press spacebar on it.
// This is in contrast to a more traditional tooltip, which you can hover over.
// I strongly recommend adding an event listener to the component that contains these toggletips, to dismiss them when the user presses
// the Escape key or clicks outside the tooltip. See `tooltip.stories.js` for how to do this.
export default function Tooltip({
  description,
  id,
  flipVertically = false,
  flipHorizontally = false,
  containerClassName,
  bubbleClassName,
  onClick,
  showToggletip,
  iconColor,
  hideIcon,
  size = 18,
  ...restProps
}: Props) {
  const { spacingProps, restProps: otherProps } = filterSpacingProps(
    restProps as Record<string, unknown>,
  )
  return (
    <Spacer
      className={cx(styles.tooltipContainer, containerClassName)}
      {...spacingProps}
    >
      <button
        onClick={onClick}
        className={cx(styles.tooltipIcon, {
          [styles.hideIcon]: hideIcon,
        })}
        type="button"
        {...otherProps}
      >
        <VisuallyHidden is="span">More information</VisuallyHidden>
        <Icon
          icon="info"
          size={size}
          color={iconColor != null ? iconColor : undefined}
        />
      </button>
      {showToggletip && (
        <div
          className={cx(styles.tooltip, bubbleClassName, {
            [styles.flipHorizontally]: flipHorizontally,
            [styles.flipVertically]: flipVertically,
            [styles.visible]: showToggletip,
          })}
          aria-live="assertive"
          role="tooltip"
          id={id}
        >
          {description}
        </div>
      )}
    </Spacer>
  )
}
